import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Star, Award, Users, Clock, Target, Lightbulb,
  Code, Rocket, CheckCircle, TrendingUp, Zap, ArrowRight,
  Shield, Database, Globe, Smartphone, Palette, Video, Share2, Monitor, Package
} from 'lucide-react';
import * as LucideIcons from "lucide-react";

// Icon Component Helper
const IconRenderer = ({ name, size = 32, className = "" }) => {
  const Icon = LucideIcons[name] || Code;
  return <Icon size={size} className={className} />;
};

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4100";

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        // Using the public endpoint we confirmed in AdminRouter
        const res = await axios.get(`${API_URL}/admin/public/services`);
        if (res.data.success) {
          setServices(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching services:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const processSteps = [
    {
      title: "Discovery",
      description: "We dive deep into your vision, requirements, and business goals through collaborative workshops.",
      icon: <Lightbulb size={32} />,
      color: "decoration-amber-400 text-amber-500 bg-amber-50"
    },
    {
      title: "Strategy",
      description: "Our experts craft a tailored roadmap, selecting the right tech stack and architecture.",
      icon: <Target size={32} />,
      color: "decoration-blue-400 text-blue-500 bg-blue-50"
    },
    {
      title: "Development",
      description: "We build your solution using agile methodologies, ensuring iterative progress and quality.",
      icon: <Code size={32} />,
      color: "decoration-emerald-400 text-emerald-500 bg-emerald-50"
    },
    {
      title: "Launch & Scale",
      description: "Seamless deployment followed by continuous monitoring and optimization for growth.",
      icon: <Rocket size={32} />,
      color: "decoration-purple-400 text-purple-500 bg-purple-50"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans selection:bg-indigo-500 selection:text-white pb-20">

      {/* HERO SECTION */}
      <div className="relative bg-slate-900 pt-32 pb-48 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-500/20 rounded-full blur-[100px] animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-fuchsia-500/20 rounded-full blur-[100px] animate-pulse delay-700"></div>
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
        </div>

        <div className="relative container mx-auto px-6 text-center z-10">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block py-1 px-3 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 font-semibold text-sm mb-6 uppercase tracking-wider"
          >
            <Star size={14} className="inline mr-2 -mt-0.5 text-yellow-400" />
            World-Class Digital Solutions
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="text-5xl md:text-7xl font-extrabold text-white mb-8 tracking-tight leading-none"
          >
            We Build <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-sky-400 to-emerald-400">The Future</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed pl-4 border-l-4 border-indigo-500/50 text-left md:text-center md:border-l-0 md:pl-0"
          >
            From cutting-edge web applications to robust enterprise software, we transform complex challenges into elegant digital realities.
          </motion.p>
        </div>

        {/* Abstract Wave at Bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="fill-slate-50" viewBox="0 0 1440 120">
            <path d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,85.3C672,75,768,85,864,96C960,107,1056,117,1152,112C1248,107,1344,85,1392,74.7L1440,64L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>
      </div>

      {/* SERVICES GRID */}
      <div className="container mx-auto px-6 -mt-32 relative z-20">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white/50 backdrop-blur-xl rounded-3xl border border-white/50 shadow-xl">
            <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-slate-500 font-semibold animate-pulse">Loading Services...</p>
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl shadow-xl">
            <div className="inline-block p-4 rounded-full bg-slate-100 mb-4">
              <Package size={32} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-lg">No active services found at the moment.</p>
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {services.map((service, index) => (
              <motion.div
                key={service._id}
                variants={itemVariants}
                whileHover={{ y: -10 }}
                className="group relative bg-white rounded-3xl p-8 shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden hover:shadow-2xl hover:shadow-indigo-500/10 transition-all duration-300"
              >
                {/* Decorative Gradient Blob */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-indigo-500/10 to-fuchsia-500/10 rounded-bl-full -mr-10 -mt-10 transition-transform group-hover:scale-150 duration-500"></div>

                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 flex items-center justify-center mb-6 group-hover:border-indigo-500/30 group-hover:from-indigo-50 group-hover:to-white transition-colors duration-300 shadow-inner">
                    <IconRenderer
                      name={service.icon}
                      size={32}
                      className="text-slate-600 group-hover:text-indigo-600 transition-colors duration-300 transform group-hover:scale-110"
                    />
                  </div>

                  <h3 className="text-2xl font-bold text-slate-800 mb-3 group-hover:text-indigo-700 transition-colors">
                    {service.title}
                  </h3>

                  <p className="text-slate-500 leading-relaxed mb-6 line-clamp-3">
                    {service.description}
                  </p>

                  {service.link && (
                    <a href={service.link} className="inline-flex items-center text-sm font-bold text-indigo-600 hover:text-indigo-800 transition-colors">
                      Learn More <ArrowRight size={16} className="ml-2 transform group-hover:translate-x-1 transition-transform" />
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {/* PROCESS SECTION */}
      <div className="container mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <span className="text-indigo-600 font-bold tracking-wider uppercase text-sm">How We Work</span>
          <h2 className="text-4xl font-extrabold text-slate-800 mt-2">Our Process</h2>
          <div className="w-20 h-1.5 bg-gradient-to-r from-indigo-500 to-fuchsia-500 mx-auto mt-4 rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {processSteps.map((step, idx) => (
            <div key={idx} className="relative group text-center">
              {idx !== processSteps.length - 1 && (
                <div className="hidden lg:block absolute top-10 left-1/2 w-full h-0.5 bg-slate-200 -z-10"></div>
              )}

              <div className={`w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-6 shadow-lg transform group-hover:rotate-6 transition-all duration-300 ${step.color}`}>
                {step.icon}
              </div>

              <h3 className="text-xl font-bold text-slate-800 mb-3">{step.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed px-4">{step.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* STATS BANNER */}
      <div className="bg-indigo-900 py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            <div className="p-4">
              <div className="text-4xl md:text-5xl font-black mb-2 text-transparent bg-clip-text bg-gradient-to-br from-white to-indigo-200">150+</div>
              <div className="text-indigo-200 font-medium tracking-wide">Projects Delivered</div>
            </div>
            <div className="p-4">
              <div className="text-4xl md:text-5xl font-black mb-2 text-transparent bg-clip-text bg-gradient-to-br from-white to-pink-200">98%</div>
              <div className="text-indigo-200 font-medium tracking-wide">Client Retention</div>
            </div>
            <div className="p-4">
              <div className="text-4xl md:text-5xl font-black mb-2 text-transparent bg-clip-text bg-gradient-to-br from-white to-emerald-200">5+</div>
              <div className="text-indigo-200 font-medium tracking-wide">Years Experience</div>
            </div>
            <div className="p-4">
              <div className="text-4xl md:text-5xl font-black mb-2 text-transparent bg-clip-text bg-gradient-to-br from-white to-amber-200">24/7</div>
              <div className="text-indigo-200 font-medium tracking-wide">Support Available</div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ServicesPage;